/** Field definitions, initial state, validation, and payload builders for Pre-UW form. */

export const FORM_STEPS = [
  'Agent',
  'Disclosure',
  'Personal Info',
  'Financial',
  'Insurance',
  'Bank',
  'Quote',
  'Health',
  'Spouse',
  'Children',
  'Agent Comments',
  'Review',
];

/** Steps when agent is pre-selected via URL (skip agent picker). */
export const FORM_STEPS_NO_PICKER = FORM_STEPS.filter((s) => s !== 'Agent');

export const ID_TYPE_OPTIONS = [
  { value: '', label: 'Select option' },
  { value: "Driver's License", label: "Driver's License" },
  { value: 'Passport', label: 'Passport' },
];

export const INSURANCE_TYPE_OPTIONS = [
  { value: '', label: 'Select option' },
  { value: 'Term', label: 'Term' },
  { value: 'Whole Life', label: 'Whole Life' },
  { value: 'UL', label: 'UL' },
  { value: 'IUL', label: 'IUL' },
  { value: 'Final Expense', label: 'Final Expense' },
];

/** Stored insurance_type value that gates family-history required fields. */
export const TERM_INSURANCE_TYPE = 'Term';

export const INSURANCE_OPTION_OPTIONS = [
  { value: '', label: 'Select option' },
  { value: 'Level', label: 'Level' },
  { value: 'Increasing', label: 'Increasing' },
];

export const RATE_CLASS_OPTIONS = [
  { value: '', label: 'Select option' },
  { value: 'Preferred', label: 'Preferred' },
  { value: 'Standard', label: 'Standard' },
  { value: 'Tobacco', label: 'Tobacco' },
];

export const APPLICATION_TYPE_OPTIONS = [
  { value: 'standalone', label: 'Standalone Policy' },
  { value: 'rider', label: 'Rider' },
];

export const GENDER_OPTIONS = ['Male', 'Female', 'Other'];

export function applicationTypeLabel(type) {
  if (type === 'standalone') return 'Standalone Policy';
  if (type === 'rider') return 'Rider';
  return type || '';
}

export const initialBeneficiary = {
  full_name: '',
  dob: '',
  ssn: '',
  relationship: '',
  percentage: '',
};

export const initialChild = {
  application_type: '',
  full_name: '',
  dob: '',
  gender: '',
  ssn: '',
  height: '',
  weight: '',
  known_conditions: '',
  medications: '',
};

export const initialForm = {
  consent_communication: false,
  consent_truthful_information: false,
  full_name: '',
  birth_state_country: '',
  birth_date: '',
  gender: '',
  ssn: '',
  address: '',
  years_at_address: '',
  cell_phone: '',
  email: '',
  citizenship: '',
  id_type: '',
  id_number: '',
  id_state: '',
  id_expiration: '',
  employer_name: '',
  employer_address: '',
  occupation: '',
  duties: '',
  employer_phone: '',
  years_at_employer: '',
  annual_income: '',
  household_income: '',
  monthly_expenses: '',
  net_worth: '',
  insurance_type: '',
  monthly_premium: '',
  insurance_option: '',
  indexed_strategy_1: '',
  indexed_strategy_2: '',
  existing_policies: '',
  beneficiaries: [{ ...initialBeneficiary }],
  bank_name: '',
  routing_number: '',
  account_number: '',
  rate_class: '',
  health_provider: '',
  last_medical_visit: '',
  height: '',
  weight: '',
  weight_change_10lbs: '',
  is_smoker: '',
  medications_5yr: '',
  other_specialists_5yr: '',
  other_specialists_detail: '',
  father_details: '',
  mother_details: '',
  siblings_details: '',
  international_travel: '',
  dangerous_activities: '',
  unpaid_tickets: '',
  exercises_regularly: '',
  health_conditions: '',
  has_spouse: false,
  spouse_application_type: '',
  spouse_full_name: '',
  spouse_birth_date: '',
  spouse_gender: '',
  spouse_ssn: '',
  spouse_citizenship: '',
  spouse_id_type: '',
  spouse_id_number: '',
  spouse_id_state: '',
  spouse_id_expiration: '',
  spouse_coverage_amount: '',
  spouse_coverage_type: '',
  spouse_health_provider: '',
  spouse_last_medical_visit: '',
  spouse_height: '',
  spouse_weight: '',
  spouse_weight_change_10lbs: '',
  spouse_is_smoker: '',
  spouse_medications_5yr: '',
  spouse_other_specialists_5yr: '',
  spouse_other_specialists_detail: '',
  spouse_international_travel: '',
  spouse_dangerous_activities: '',
  spouse_unpaid_tickets: '',
  spouse_exercises_regularly: '',
  spouse_health_conditions: '',
  has_children: false,
  children: [],
  agent_name_code: '',
  split_agent_name_code: '',
  agent_carrier: '',
  agent_indexed_strategies: '',
  agent_other_comments: '',
};

/** CRM schema spouse columns (excludes Hub-only family history fields not in migration). */
export const spouseFields = [
  'spouse_application_type',
  'spouse_full_name',
  'spouse_birth_date',
  'spouse_gender',
  'spouse_ssn',
  'spouse_citizenship',
  'spouse_id_type',
  'spouse_id_number',
  'spouse_id_state',
  'spouse_id_expiration',
  'spouse_coverage_amount',
  'spouse_coverage_type',
  'spouse_health_provider',
  'spouse_last_medical_visit',
  'spouse_height',
  'spouse_weight',
  'spouse_weight_change_10lbs',
  'spouse_is_smoker',
  'spouse_medications_5yr',
  'spouse_other_specialists_5yr',
  'spouse_other_specialists_detail',
  'spouse_international_travel',
  'spouse_dangerous_activities',
  'spouse_unpaid_tickets',
  'spouse_exercises_regularly',
  'spouse_health_conditions',
];

export const booleanFieldKeys = [
  'weight_change_10lbs',
  'is_smoker',
  'other_specialists_5yr',
  'international_travel',
  'dangerous_activities',
  'unpaid_tickets',
  'exercises_regularly',
  'spouse_weight_change_10lbs',
  'spouse_is_smoker',
  'spouse_other_specialists_5yr',
  'spouse_international_travel',
  'spouse_dangerous_activities',
  'spouse_unpaid_tickets',
  'spouse_exercises_regularly',
];

export const dateFieldKeys = ['birth_date', 'id_expiration', 'spouse_birth_date', 'spouse_id_expiration'];

export const toNullableBoolean = (value) => {
  if (value === true || value === false) return value;
  if (value === 'Yes') return true;
  if (value === 'No') return false;
  return null;
};

export const toNullableDate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  return value;
};

export const summarySections = [
  {
    title: 'Disclosure & Privacy',
    fields: [
      ['Communication Consent', 'consent_communication'],
      ['Statement Confirmed', 'consent_truthful_information'],
    ],
  },
  {
    title: 'Personal Information',
    fields: [
      ['Full Name', 'full_name'],
      ['Birth State/Country', 'birth_state_country'],
      ['Birth Date', 'birth_date'],
      ['Gender', 'gender'],
      ['SSN/ITIN', 'ssn'],
      ['Address', 'address'],
      ['Years At Address', 'years_at_address'],
      ['Cell Phone', 'cell_phone'],
      ['Email', 'email'],
      ['Citizenship', 'citizenship'],
      ['ID Type', 'id_type'],
      ['ID Number', 'id_number'],
      ['ID State', 'id_state'],
      ['ID Expiration', 'id_expiration'],
    ],
  },
  {
    title: 'Employment',
    fields: [
      ['Employer Name', 'employer_name'],
      ['Employer Address', 'employer_address'],
      ['Occupation', 'occupation'],
      ['Duties', 'duties'],
      ['Employer Phone', 'employer_phone'],
      ['Years At Employer', 'years_at_employer'],
    ],
  },
  {
    title: 'Financial Information',
    fields: [
      ['Annual Income', 'annual_income'],
      ['Household Income', 'household_income'],
      ['Monthly Expenses', 'monthly_expenses'],
      ['Net Worth', 'net_worth'],
    ],
  },
  {
    title: 'Insurance Information',
    fields: [
      ['Insurance Type', 'insurance_type'],
      ['Monthly Premium', 'monthly_premium'],
      ['Insurance Option', 'insurance_option'],
      ['Indexed Strategy 1', 'indexed_strategy_1'],
      ['Indexed Strategy 2', 'indexed_strategy_2'],
      ['Existing Policies', 'existing_policies'],
    ],
  },
  {
    title: 'Bank Information',
    fields: [
      ['Bank Name', 'bank_name'],
      ['Routing Number', 'routing_number'],
      ['Account Number', 'account_number'],
    ],
  },
  {
    title: 'Illustration / Quote',
    fields: [['Rate Class', 'rate_class']],
  },
  {
    title: 'Health Questionnaire (Primary)',
    fields: [
      ['Health Provider', 'health_provider'],
      ['Last Medical Visit', 'last_medical_visit'],
      ['Height', 'height'],
      ['Weight', 'weight'],
      ['Weight Change 10lbs', 'weight_change_10lbs'],
      ['Is Smoker', 'is_smoker'],
      ['Medications 5yr', 'medications_5yr'],
      ['Other Specialists 5yr', 'other_specialists_5yr'],
      ['Other Specialists Detail', 'other_specialists_detail'],
      ['Father Details', 'father_details'],
      ['Mother Details', 'mother_details'],
      ['Siblings Details', 'siblings_details'],
      ['International Travel', 'international_travel'],
      ['Dangerous Activities', 'dangerous_activities'],
      ['Unpaid Tickets', 'unpaid_tickets'],
      ['Exercises Regularly', 'exercises_regularly'],
      ['Health Conditions', 'health_conditions'],
    ],
  },
  {
    title: 'Agent Comments',
    fields: [
      ['Agent Name/Code', 'agent_name_code'],
      ['Split Agent Name/Code', 'split_agent_name_code'],
      ['Agent Carrier', 'agent_carrier'],
      ['Agent Indexed Strategies', 'agent_indexed_strategies'],
      ['Agent Other Comments', 'agent_other_comments'],
    ],
  },
];

/**
 * Step navigation no longer blocks on required fields — validation is submit-only.
 * Kept for API compatibility; always returns true.
 */
export function validateStep(_form, _stepIndex, _stepLabels) {
  return true;
}

function isFilled(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === 'boolean') return true;
  if (typeof value === 'string') return value.trim().length > 0;
  return Boolean(value);
}

const STEP_SECTION_LABELS = {
  Agent: 'Agent',
  Disclosure: 'Disclosure',
  'Personal Info': 'Personal Information',
  Financial: 'Financial',
  Insurance: 'Insurance',
  Bank: 'Bank',
  Quote: 'Quote',
  Health: 'Health Questionnaire',
  Spouse: 'Spouse',
  Children: 'Children',
  'Agent Comments': 'Agent Comments',
  Review: 'Review',
};

/**
 * Consolidated required-field check for final Submit & Generate PDF.
 * @returns {{ label: string, step: string, section: string, stepIndex: number, field: string }[]}
 */
export function collectSubmitValidationErrors(form, stepLabels) {
  const errors = [];
  const stepIndexOf = (name) => {
    const idx = stepLabels.indexOf(name);
    return idx >= 0 ? idx : 0;
  };
  const add = (field, label, step, section = STEP_SECTION_LABELS[step] || step) => {
    errors.push({ field, label, step, section, stepIndex: stepIndexOf(step) });
  };

  if (!form.__agent_id) {
    add('__agent_id', 'Agent', stepLabels.includes('Agent') ? 'Agent' : 'Disclosure');
  }

  if (!form.consent_communication) {
    add('consent_communication', 'Communication Consent', 'Disclosure');
  }
  if (!form.consent_truthful_information) {
    add('consent_truthful_information', 'Statement Confirmed', 'Disclosure');
  }

  const personalRequired = [
    ['full_name', 'Full Name'],
    ['birth_state_country', 'Birth State/Country'],
    ['birth_date', 'Birth Date'],
    ['gender', 'Gender'],
    ['ssn', 'SSN/ITIN'],
    ['address', 'Address'],
    ['cell_phone', 'Cell Phone'],
    ['email', 'Email'],
    ['citizenship', 'Citizenship'],
    ['id_type', 'ID Type'],
    ['id_number', 'ID Number'],
    ['id_expiration', 'ID Expiration'],
  ];
  for (const [field, label] of personalRequired) {
    if (!isFilled(form[field])) add(field, label, 'Personal Info');
  }

  const employmentRequired = [
    ['employer_name', 'Employer Name'],
    ['occupation', 'Occupation'],
    ['years_at_employer', 'Years At Employer'],
  ];
  for (const [field, label] of employmentRequired) {
    if (!isFilled(form[field])) add(field, label, 'Personal Info', 'Employment');
  }

  if (!isFilled(form.annual_income)) add('annual_income', 'Annual Income', 'Financial');
  if (!isFilled(form.household_income)) {
    add('household_income', 'Household Income', 'Financial');
  }

  if (!isFilled(form.insurance_type)) add('insurance_type', 'Insurance Type', 'Insurance');
  if (!isFilled(form.insurance_option)) {
    add('insurance_option', 'Insurance Option', 'Insurance');
  }

  // Beneficiary keys: full_name, dob, ssn, relationship, percentage
  const beneficiaries = Array.isArray(form.beneficiaries) ? form.beneficiaries : [];
  const firstWithData = beneficiaries.find(
    (entry) => entry && (entry.full_name || entry.dob || entry.ssn || entry.relationship || entry.percentage),
  );
  const primaryBeneficiary = firstWithData || beneficiaries[0];
  if (!primaryBeneficiary || !isFilled(primaryBeneficiary.full_name) || !isFilled(primaryBeneficiary.dob)) {
    add('beneficiaries', 'Beneficiary name and date of birth', 'Insurance');
  }

  if (!isFilled(form.bank_name)) add('bank_name', 'Bank Name', 'Bank');
  if (!isFilled(form.routing_number)) add('routing_number', 'Routing Number', 'Bank');
  if (!isFilled(form.account_number)) add('account_number', 'Account Number', 'Bank');

  if (!isFilled(form.rate_class)) add('rate_class', 'Rate Class', 'Quote');

  const healthRequired = [
    ['health_provider', 'Health Provider'],
    ['last_medical_visit', 'Last Medical Visit'],
    ['height', 'Height'],
    ['weight', 'Weight'],
    ['is_smoker', 'Is Smoker'],
    ['medications_5yr', 'Medications (Last 5 Years)'],
    ['health_conditions', 'Health Conditions'],
  ];
  for (const [field, label] of healthRequired) {
    if (!isFilled(form[field])) add(field, label, 'Health');
  }

  if (form.insurance_type === TERM_INSURANCE_TYPE) {
    if (!isFilled(form.father_details)) add('father_details', 'Father Details', 'Health');
    if (!isFilled(form.mother_details)) add('mother_details', 'Mother Details', 'Health');
    if (!isFilled(form.siblings_details)) add('siblings_details', 'Siblings Details', 'Health');
  }

  if (form.has_spouse) {
    if (!isFilled(form.spouse_application_type)) {
      add('spouse_application_type', 'Spouse Application Type', 'Spouse');
    }
    if (!isFilled(form.spouse_full_name)) add('spouse_full_name', 'Spouse Full Name', 'Spouse');
    if (!isFilled(form.spouse_birth_date)) add('spouse_birth_date', 'Spouse Birth Date', 'Spouse');
    if (!isFilled(form.spouse_gender)) add('spouse_gender', 'Spouse Gender', 'Spouse');
  }

  if (form.has_children) {
    const children = Array.isArray(form.children) ? form.children : [];
    if (
      children.length === 0 ||
      !children.every((entry) => entry.application_type && entry.full_name && entry.dob)
    ) {
      add('children', 'Child application type, name, and date of birth', 'Children');
    }
  }

  if (!isFilled(form.agent_name_code)) {
    add('agent_name_code', 'Agent Name/Code', 'Agent Comments');
  }

  return errors;
}

/** Format a submit validation error for display. */
export function formatSubmitValidationError(err) {
  return `${err.label} — missing (${err.section || err.step})`;
}

/**
 * Map a decrypted draft payload into form state (Yes/No for radio booleans, dates as strings).
 * Early-saved drafts legitimately have many nulls — never let those overwrite controlled-input defaults.
 */
export function hydrateFormFromDraft(draft, { agentIdParam } = {}) {
  const src = draft && typeof draft === 'object' ? draft : {};
  const next = { ...initialForm };

  for (const [key, value] of Object.entries(src)) {
    if (value == null) continue; // keep initialForm default ('' / false / [])
    if (key in next || key.startsWith('__')) {
      next[key] = value;
    }
  }

  next.__agent_id = src.agent_id || agentIdParam || '';
  next.__split_agent_id = src.split_agent_id || '';
  next.beneficiaries =
    Array.isArray(src.beneficiaries) && src.beneficiaries.length
      ? src.beneficiaries.map((b) => ({ ...initialBeneficiary, ...(b && typeof b === 'object' ? b : {}) }))
      : [{ ...initialBeneficiary }];
  next.children = Array.isArray(src.children)
    ? src.children.map((c) => ({ ...initialChild, ...(c && typeof c === 'object' ? c : {}) }))
    : [];
  next.has_spouse = Boolean(src.has_spouse);
  next.has_children = Boolean(src.has_children);
  next.consent_communication = Boolean(src.consent_communication);
  next.consent_truthful_information = Boolean(src.consent_truthful_information);

  for (const key of booleanFieldKeys) {
    const v = src[key];
    if (v === true) next[key] = 'Yes';
    else if (v === false) next[key] = 'No';
    else next[key] = '';
  }

  for (const key of dateFieldKeys) {
    const v = src[key];
    if (v == null || v === '') next[key] = '';
    else next[key] = String(v).slice(0, 10);
  }

  // Clear non-form metadata from draft
  delete next.id;
  delete next.agency_id;
  delete next.agent_id;
  delete next.split_agent_id;
  delete next.status;
  delete next.last_saved_at;

  return next;
}

export function buildSubmitPayload(form, { agencyId, agentId, splitAgentId }) {
  const payload = {
    ...form,
    agency_id: agencyId,
    agent_id: agentId,
    split_agent_id: splitAgentId || null,
    beneficiaries: (form.beneficiaries || []).filter(
      (entry) => entry.full_name || entry.relationship || entry.percentage || entry.ssn || entry.dob,
    ),
    children: form.has_children ? form.children || [] : [],
  };

  delete payload.__agent_id;
  delete payload.__split_agent_id;
  delete payload.children_riders;
  delete payload.has_spouse_rider;
  delete payload.has_children_riders;

  booleanFieldKeys.forEach((key) => {
    payload[key] = toNullableBoolean(payload[key]);
  });
  dateFieldKeys.forEach((key) => {
    payload[key] = toNullableDate(payload[key]);
  });

  payload.consent_communication = Boolean(form.consent_communication);
  payload.consent_truthful_information = Boolean(form.consent_truthful_information);
  payload.has_spouse = Boolean(form.has_spouse);
  payload.has_children = Boolean(form.has_children);
  payload.spouse_application_type = form.has_spouse
    ? form.spouse_application_type || null
    : null;

  if (!form.has_spouse) {
    spouseFields.forEach((key) => {
      payload[key] = null;
    });
    payload.spouse_application_type = null;
  }

  return payload;
}

export function summaryValue(value) {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? JSON.stringify(value) : '-';
  return String(value);
}

export function sanitizeFilePart(value = 'Application') {
  return (
    String(value)
      .replace(/[^a-zA-Z0-9-_ ]/g, '')
      .trim()
      .replace(/\s+/g, '_') || 'Application'
  );
}
